import { useCallback } from 'react';

import { Modal, ModalConfirmButton, ModalWidth, StyledFlex } from '@dreryk/react-components';
import { createInterpolatedTranslationKey } from '@dreryk/react-common';

import { translationKeys } from '../translations';

import { getElapsedTimeParts } from './formatElapsedTime';
import { AiAssistantMainMenuTileItemSummaryInformation } from './AiAssistantMainMenuTileItemSummaryInformation';
import { AiAssistantMainMenuTileItemSummaryUnassignedNotes } from './AiAssistantMainMenuTileItemSummaryUnassignedNotes';
import { AiAssistantMainMenuTileItemSummaryForm } from './AiAssistantMainMenuTileItemSummaryForm';
import { formInitializer } from './AiAssistantMainMenuTileItemSummaryForm.utils';
import { AiAssistantMainMenuTileItemSummaryFormAcceptSummaryCallback } from './AiAssistantMainMenuTileItemSummaryForm.types';
import {
   AiAssistantMainMenuTileItemSummaryModalAcceptSummaryCallback,
   AiAssistantMainMenuTileItemSummaryModalResult,
} from './AiAssistantMainMenuTileItemSummaryModal.types';

const t = translationKeys.mainMenuTileItem.summaryModal;

type AiAssistantMainMenuTileItemSummaryModalProps = {
   result: AiAssistantMainMenuTileItemSummaryModalResult;
   accumulatedSeconds: number;
   onClose: VoidFunction;
   onAcceptSummary: AiAssistantMainMenuTileItemSummaryModalAcceptSummaryCallback;
};

export const AiAssistantMainMenuTileItemSummaryModal = (
   props: AiAssistantMainMenuTileItemSummaryModalProps
): JSX.Element => {
   const handleAcceptSummary: AiAssistantMainMenuTileItemSummaryFormAcceptSummaryCallback = useCallback(
      (formValues) => {
         props.onAcceptSummary({
            examination: formValues.transferExamination ? formValues.examination?.getFormattedValue() ?? '' : '',
            interview: formValues.transferInterview ? formValues.interview?.getFormattedValue() ?? '' : '',
            recommendations: formValues.transferRecommendations
               ? formValues.recommendations?.getFormattedValue() ?? ''
               : '',
         });
         props.onClose();
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [props.onAcceptSummary, props.onClose]
   );

   return (
      <Modal
         testId={'transcriptSummary'}
         title={createInterpolatedTranslationKey(t.title, getElapsedTimeParts(props.accumulatedSeconds))}
         onCancel={props.onClose}
         onCloseRequest={props.onClose}
         cancelButtonLabel={t.close}
         width={ModalWidth.WIDE}
         shouldCloseOnEsc
         shouldCloseOnOverlayClick
      >
         <StyledFlex direction={'column'} gap={16}>
            <AiAssistantMainMenuTileItemSummaryInformation />
            <AiAssistantMainMenuTileItemSummaryForm initializer={formInitializer(props.result)}>
               {({ handleSubmit }) => (
                  <ModalConfirmButton label={t.accept} onClick={handleSubmit(handleAcceptSummary)} />
               )}
            </AiAssistantMainMenuTileItemSummaryForm>
            {props.result?.unassigned ? (
               <AiAssistantMainMenuTileItemSummaryUnassignedNotes unassigned={props.result.unassigned} />
            ) : null}
         </StyledFlex>
      </Modal>
   );
};
